import { useMemo, useState, useRef, useEffect } from "react";
import { Row, Col, CardBody, Spinner } from "reactstrap";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { showSuccess, showError, showInfo } from "../components/AppToast";

import {
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  Download,
  X,
  RefreshCcw,
} from "lucide-react";
import DateRangeInput from "../components/DateRangeInput";
import api from "../api";
import Sidebar from "../components/Sidebar";

// yyyy-MM-dd in LOCAL time (avoids toISOString timezone shift)
const toApiDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

// Word sent in `searchText` when a status capsule is clicked.
// Change the pending word here if the backend expects "bank" / "pending from bank".
const STATUS_SEARCH_TERMS = {
  success: "success",
  pending: "pending",
  failed: "failed",
};

// Reads the CSRF token the backend sets (cookie), falling back to storage / meta tag
const getCsrfToken = () => {
  const match = document.cookie.match(
    /(?:^|;\s*)(?:XSRF-TOKEN|CSRF-TOKEN|csrfToken)=([^;]*)/i,
  );
  if (match) return decodeURIComponent(match[1]);

  const stored =
    localStorage.getItem("csrfToken") || sessionStorage.getItem("csrfToken");
  if (stored) return stored;

  return document.querySelector('meta[name="_csrf"]')?.content || "";
};

const EMPTY_CAPSULE_COUNTS = {
  all: null,
  success: null,
  pending: null,
  failed: null,
};

// Escapes regex special characters so the search text is matched literally
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Browser-style search term highlighting (wraps matches in <mark>)
const HighlightText = ({ text, term }) => {
  const str = text === null || text === undefined ? "" : String(text);
  const t = (term || "").trim();
  if (!t || !str) return <>{str}</>;

  const parts = str.split(new RegExp(`(${escapeRegExp(t)})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === t.toLowerCase() ? (
          <mark
            key={i}
            style={{
              backgroundColor: "#fde68a",
              color: "inherit",
              padding: 0,
              borderRadius: 2,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const { slug } = useParams(); // Get slug from URL params
  const searchRef = useRef(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;
  const [status, setStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadingButtons, setLoadingButtons] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [labeledData, setLabeledData] = useState([]);
  const [paginationReset, setPaginationReset] = useState(false);

  // Count shown on each capsule. Only "All" is known initially;
  // a capsule's count appears once it has been clicked (taken from the API total).
  const [capsuleCounts, setCapsuleCounts] = useState(EMPTY_CAPSULE_COUNTS);

  // Overall metrics (summary boxes) — aggregated from the monthly metrics API
  const [metrics, setMetrics] = useState({
    total: 0,
    success: 0,
    pending: 0,
    failed: 0,
  });
  const [metricsLoading, setMetricsLoading] = useState(true);

  const lastSearchRef = useRef("");
  const fetchIdRef = useRef(0);

  // Date range is only applied once both dates are picked
  const startStr = startDate && endDate ? toApiDate(startDate) : "";
  const endStr = startDate && endDate ? toApiDate(endDate) : "";

  // Term highlighted in the table — only the typed search (not status capsule keywords)
  const highlightTerm = status === "all" ? debouncedSearch : "";

  // Status code definitions
  const SUCCESS_CODES = ["OTS0000", "OTS0002"];
  const PENDING_CODES = ["OTS0201", "OTS0301", "OTS0351", "OTS0551"];
  const FAILED_CODES = [
    "OTS0001",
    "OTS0101",
    "OTS0401",
    "OTS0503",
    "OTS0504",
    "OTS0506",
    "OTS0507",
    "OTS0508",
    "OTS0509",
    "OTS0510",
    "OTS0522",
    "OTS0600",
  ];

  // Focus search input on mount
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Fetch overall transaction metrics (total / success / pending / failed) for the four boxes
  useEffect(() => {
    const fetchMetrics = async () => {
      const token = localStorage.getItem("jwt");
      if (!token) {
        showError("Session timed out");
        navigate("/login");
        return;
      }

      setMetricsLoading(true);
      try {
        const res = await api.post(
          "payment/fetchDashBoardDetails",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        const d = res.data || {};

        setMetrics({
          total: Number(d.totalTransactions) || 0,
          success: Number(d.successTransactions) || 0,
          pending: Number(d.pendingTransactions) || 0,
          failed: Number(d.failedTransactions) || 0,
        });
      } catch (err) {
        console.error("Error fetching metrics:", err);
        showError("Failed to load transaction metrics");
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [navigate]);

  // Debounce search → API; reset to page 1 when the search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = search.trim();
      if (next !== lastSearchRef.current) {
        lastSearchRef.current = next;
        setDebouncedSearch(next);
        setCurrentPage(1);
        setPaginationReset((p) => !p);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // When search or date range changes, previously loaded capsule counts are stale
  useEffect(() => {
    setCapsuleCounts(EMPTY_CAPSULE_COUNTS);
  }, [debouncedSearch, startStr, endStr]);

  const handleDateChange = (update) => {
    setRange(update);
    const [s, e] = update;
    // Only reset page when the range is complete or fully cleared
    if ((s && e) || (!s && !e)) {
      setCurrentPage(1);
      setPaginationReset((p) => !p);
    }
  };

  // Typing in the search box goes back to the "All" capsule
  const handleSearchChange = (value) => {
    setSearch(value);
    if (status !== "all") {
      setStatus("all");
      setCurrentPage(1);
      setPaginationReset((p) => !p);
    }
  };

  // Clicking a capsule fetches that status from the API (server-side)
  const handleStatusChange = (next) => {
    if (next === status) return;

    // Status uses the search param, so clear any typed search
    setSearch("");
    lastSearchRef.current = "";
    setDebouncedSearch("");

    setStatus(next);
    setCurrentPage(1);
    setPaginationReset((p) => !p);
  };

  // Helper function to parse form_response and extract status details
  const parseFormResponse = (formResponse) => {
    try {
      const parsed =
        typeof formResponse === "string"
          ? JSON.parse(formResponse)
          : formResponse || {};

      if (parsed.payInstrument && parsed.payInstrument.length > 0) {
        const firstInstrument = parsed.payInstrument[0];
        const responseDetails = firstInstrument.responseDetails;
        const payDetails = firstInstrument.payDetails || {};

        return {
          statusCode: responseDetails?.statusCode || "PENDING",
          message: responseDetails?.message || "Pending",
          description: responseDetails?.description || "",
          atomTxnId: payDetails?.atomTxnId,
          amount: payDetails?.amount,
          totalAmount: payDetails?.totalAmount,
        };
      }

      if (parsed.responseDetails) {
        return {
          statusCode: parsed.responseDetails.statusCode || "PENDING",
          message: parsed.responseDetails.message || "Pending",
          description: parsed.responseDetails.description || "",
        };
      }

      if (parsed.payInstrument?.payDetails) {
        return {
          statusCode: parsed.payInstrument.payDetails.statusCode || "PENDING",
          message: parsed.payInstrument.payDetails.statusMessage || "Pending",
          description: "",
          atomTxnId: parsed.payInstrument.payDetails.atomTxnId,
          amount: parsed.payInstrument.payDetails.amount,
          totalAmount: parsed.payInstrument.payDetails.totalAmount,
        };
      }

      return null;
    } catch (e) {
      console.error("Error parsing form_response:", e);
      return null;
    }
  };

  const SkeletonRow = ({ cols }) => (
    <tr className="skeleton-row">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}>
          <div className="skeleton-cell" />
        </td>
      ))}
    </tr>
  );

  const SkeletonTable = ({ rows = 8, cols = 5 }) => (
    <div className="skeleton-table-wrap">
      <table className="skeleton-table">
        <thead>
          <tr>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i}>
                <div className="skeleton-cell skeleton-header" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );

  const parseFormRequest = (formRequestStr) => {
    try {
      const cleaned = formRequestStr.startsWith('"')
        ? JSON.parse(formRequestStr)
        : formRequestStr;
      return typeof cleaned === "string" ? JSON.parse(cleaned) : cleaned;
    } catch (err) {
      console.error("Error parsing form_request:", err);
      return {};
    }
  };

  useEffect(() => {
    // Date range half-picked (only start date) — wait for the end date
    if (startDate && !endDate) return;

    const fetchData = async () => {
      const token = localStorage.getItem("jwt");
      if (!token) {
        showError("Session timed out");
        navigate("/login");
        return;
      }

      // Status capsules send their keyword; otherwise use the typed search
      const effectiveSearch =
        status !== "all" ? STATUS_SEARCH_TERMS[status] : debouncedSearch;

      const requestId = ++fetchIdRef.current;
      setIsLoading(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: currentPage - 1,
            size: rowsPerPage,
            searchText: effectiveSearch || undefined,
            startDate: startStr || undefined,
            endDate: endStr || undefined,
          },
        };

        const res = await api.post("/forms/fetchByTransactions", {}, config);

        if (requestId !== fetchIdRef.current) return;

        if (res.status === 203) {
          showError("You are not authorize to view this page");
          return;
        }

        const apiData = Array.isArray(res.data)
          ? res.data
          : res.data?.content || [];

        const total = Array.isArray(res.data)
          ? apiData.length
          : res.data?.totalElements ?? apiData.length;
        setTotalRows(total);

        // Show the count for the capsule that is currently active
        setCapsuleCounts((prev) => ({ ...prev, [status]: total }));

        const formattedData = apiData.map((row) => {
          return {
            id: row.id,
            atomTxnId: row.atomTxnId || "N/A",
            atomTokenId: row.atomTokenId || "N/A",
            createdAt: row.createdAt || "N/A",
            formId: row.formId,
            merchantId: row.merchantId || "N/A",
            merchantTransactionId: row.merchantTransactionId || "N/A",
            transactionStatusCode: row.transactionStatusCode || "N/A",
            transactionMessage: row.transactionMessage || "N/A",
            transactionDescription: row.transactionDiscription || "N/A",
            amount: row.amount || "N/A",
            total_amount: row.total_amount || "N/A",
            customer_email: row.customer_email || "N/A",
            customer_mobile: row.customer_mobile || "N/A",
            form_response: row.form_response,
            form_request: row.form_request,
            updatedAt: row.updatedAt,
            slug: row.slug || "N/A",
            merchant: row.merchantId || "N/A",
            date: row.createdAt
              ? new Date(row.createdAt).toLocaleString()
              : "N/A",
          };
        });

        setData(formattedData);
      } catch (err) {
        if (requestId !== fetchIdRef.current) return;
        console.error("Error fetching transactions:", err);
        navigate("/login");
      } finally {
        if (requestId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [
    navigate,
    currentPage,
    rowsPerPage,
    debouncedSearch,
    status,
    startDate,
    endDate,
    startStr,
    endStr,
  ]);

  // Helper functions for status classification
  const isSuccess = (code) => SUCCESS_CODES.includes(code);
  const isPending = (code) =>
    !code ||
    code === "PENDING" ||
    code === "N/A" ||
    PENDING_CODES.includes(code);
  const isFailed = (code) => FAILED_CODES.includes(code);
  const isUnknown = (code) => !code || code === "N/A" || code.trim() === "";

  // Filtering is done by the API now, so the loaded page is shown as-is
  const filteredData = data;

  // Handle refresh status
  const handleRefreshStatus = async (merchantTransactionId) => {
    setLoadingButtons((prev) => ({ ...prev, [merchantTransactionId]: true }));

    try {
      const token = localStorage.getItem("jwt");

      const response = await api.post(
        "/forms/transactionStatus",
        { merchantTransactionId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const apiData = response.data;
      const parsedDetails = parseFormResponse(apiData);

      if (!parsedDetails) {
        showError("Invalid response format from status check");
        return;
      }

      const newCode = parsedDetails.statusCode;

      setData((prev) =>
        prev.map((item) => {
          if (item.merchantTransactionId === merchantTransactionId) {
            return {
              ...item,
              transactionStatusCode: newCode || "N/A",
              transactionMessage: parsedDetails.message || "N/A",
              transactionDescription: parsedDetails.description || "",
              atomTxnId: parsedDetails.atomTxnId || item.atomTxnId,
              amount: parsedDetails.amount || item.amount,
              total_amount: parsedDetails.totalAmount || item.total_amount,
              updatedAt: new Date().toISOString(),
              form_response: JSON.stringify(apiData),
            };
          }
          return item;
        }),
      );

      if (SUCCESS_CODES.includes(newCode)) {
        showSuccess(`Transaction successful: ${parsedDetails.message}`);
      } else if (PENDING_CODES.includes(newCode) || newCode === "PENDING") {
        showInfo(`Transaction status: ${parsedDetails.message || "Pending"}`);
      } else if (FAILED_CODES.includes(newCode)) {
        showError(`Transaction status: ${parsedDetails.message}`);
      } else {
        showInfo(`Status updated: ${parsedDetails.message}`);
      }
    } catch (error) {
      console.error("Error refreshing transaction status:", error);
      showError("Failed to refresh transaction status");
    } finally {
      setLoadingButtons((prev) => ({
        ...prev,
        [merchantTransactionId]: false,
      }));
    }
  };

  const handleViewReceipt = (transaction_id, slug) => {
    navigate(`/${slug}/receipt/${transaction_id}`);
  };

  // Status color helper
  const getStatusColor = (code) => {
    if (isSuccess(code)) return "#16a34a";
    if (isPending(code) || isUnknown(code)) return "#f59e0b";
    if (isFailed(code)) return "#dc2626";
    return "#64748b";
  };

  const labeledColumns = useMemo(() => {
    if (labeledData.length === 0) return [];

    const keys = Object.keys(labeledData[0]);
    return keys.map((key) => ({
      name: key,
      selector: (row) => row[key] ?? "N/A",
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <HighlightText text={row[key] ?? "N/A"} term={highlightTerm} />
      ),
    }));
  }, [labeledData, highlightTerm]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        name: "Sr No",
        cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
        sortable: false,
        width: "80px",
        center: true,
      },
      {
        name: "Atom Txn ID",
        selector: (row) => row.atomTxnId,
        sortable: true,
        minWidth: "150px",
        cell: (row) => <HighlightText text={row.atomTxnId} term={highlightTerm} />,
      },
      {
        name: "Created At",
        selector: (row) => row.createdAt,
        sortable: true,
        minWidth: "180px",
        cell: (row) =>
          row.createdAt ? (
            <HighlightText
              text={new Date(row.createdAt).toLocaleString()}
              term={highlightTerm}
            />
          ) : (
            "N/A"
          ),
      },
      {
        name: "Merchant ID",
        selector: (row) => row.merchant,
        sortable: true,
        minWidth: "120px",
        cell: (row) => <HighlightText text={row.merchant} term={highlightTerm} />,
      },
      {
        name: "Transaction ID",
        selector: (row) => row.merchantTransactionId,
        sortable: true,
        minWidth: "240px",
        maxWidth: "240px",
         cell: (row) => (
          <div
            title={row.merchantTransactionId}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",

            }}
          >
            <HighlightText
              text={row.merchantTransactionId}
              term={highlightTerm}
            />
          </div>
        ),
      },
      {
        name: "Amount",
        selector: (row) => row.amount,
        sortable: true,
        minWidth: "100px",
        cell: (row) =>
          row.amount !== "N/A" ? (
            <>
              ₹<HighlightText text={row.amount} term={highlightTerm} />
            </>
          ) : (
            "N/A"
          ),
      },
      {
        name: "Customer Email",
        selector: (row) => row.customer_email,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
           <div
            title={row.customer_email}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",

            }}
          >
          <HighlightText text={row.customer_email} term={highlightTerm} />
          </div>
        ),
      },
      {
        name: "Customer Mobile",
        selector: (row) => row.customer_mobile,
        sortable: true,
        minWidth: "180px",
        cell: (row) => (
          <HighlightText text={row.customer_mobile} term={highlightTerm} />
        ),
      },
      {
        name: "Status",
        selector: (row) => row.transactionMessage,
        sortable: true,
        width: "120px",
        cell: (row) => (
          <span
            style={{
              color: getStatusColor(row.transactionStatusCode),
              fontWeight: 500,
            }}
          >
            <HighlightText text={row.transactionMessage} term={highlightTerm} />
          </span>
        ),
      },
      {
        name: "URL (Slug)",
        selector: (row) => row.slug,
        sortable: true,
        width: "120px",
        cell: (row) => (
           <div
            title={row.slug}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",

            }}
          >
          <HighlightText text={row.slug} term={highlightTerm} />
          </div>
        ),
      },
      {
        name: "Actions",
        minWidth: "100px",
        cell: (row) => {
          const isRefreshing = loadingButtons[row.merchantTransactionId];
          const code = row.transactionStatusCode || "";
          const isSuccessRow = SUCCESS_CODES.includes(code);
          const isUnknownRow = !code || code === "N/A" || code.trim() === "";

          if (isSuccessRow) {
            return (
              <button
                className="master-action-button receipt"
                onClick={() =>
                  handleViewReceipt(row.merchantTransactionId, row.slug)
                }
                title="View Receipt"
              >
                <FileText size={16} />
              </button>
            );
          }

          if (isUnknownRow) {
            return (
              <button
                className="master-action-button refresh"
                disabled={true}
                title="Status not available"
                style={{ opacity: 0.4, cursor: "not-allowed" }}
              >
                <RefreshCcw size={16} />
              </button>
            );
          }

          return (
            <button
              className="master-action-button refresh"
              onClick={() => handleRefreshStatus(row.merchantTransactionId)}
              title={isRefreshing ? "Refreshing..." : "Refresh Status"}
              disabled={isRefreshing}
              style={{
                opacity: isRefreshing ? 0.6 : 1,
                cursor: isRefreshing ? "not-allowed" : "pointer",
              }}
            >
              <RefreshCcw
                size={16}
                style={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                }}
              />
            </button>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [loadingButtons, currentPage, rowsPerPage, slug, highlightTerm],
  );

  const allColumns = useMemo(() => {
    if (labeledColumns.length === 0) {
      return columns;
    }

    const amountIndex = columns.findIndex((col) => col.name === "Amount");
    const beforeAmount = columns.slice(0, amountIndex);
    const afterAmount = columns.slice(amountIndex);
    const merged = [...beforeAmount, ...labeledColumns, ...afterAmount];

    return merged;
  }, [columns, labeledColumns]);

  const transformTxnRow = (row, srNo, labeledColumns) => {
    const base = {
      srNo,
      amount: row.amount || "",
      atomTokenId: row.atomTokenId || "",
      atomTxnId: row.atomTxnId || "",
      createdAt: row.createdAt
        ? new Date(row.createdAt).toLocaleString()
        : "N/A",
      customer_email: row.customer_email || "",
      customer_mobile: row.customer_mobile || "",
      formId: row.formId || "",
      merchantId: row.merchantId || "",
      merchantTransactionId: row.merchantTransactionId || "",
      total_amount: row.total_amount ? `₹${row.total_amount}` : "N/A",
      transactionMessage: row.transactionMessage || "",
      transactionStatusCode: row.transactionStatusCode || "",
      updatedAt: row.updatedAt
        ? new Date(row.updatedAt).toLocaleString()
        : "N/A",
    };

    return { ...base };
  };

  // NOTE: this exports only the rows currently loaded (current page).

  const baseHeaders = [
    { label: "Sr No", key: "srNo" },
    { label: "Atom Txn ID", key: "atomTxnId" },
    { label: "Created At", key: "createdAt" },
    { label: "Merchant ID", key: "merchantId" },
    { label: "Transaction ID", key: "merchantTransactionId" },
    { label: "Total Amount", key: "total_amount" },
    { label: "Customer Email", key: "customer_email" },
    { label: "Customer Mobile", key: "customer_mobile" },
    { label: "Transaction Status", key: "transactionMessage" },
  ];

  const exportAllTransactions = async () => {
    if (isExporting) return;

    const token = localStorage.getItem("jwt");
    if (!token) {
      showError("Session timed out");
      navigate("/login");
      return;
    }

    setIsExporting(true);
    try {
      const csrfToken = getCsrfToken();

      const response = await api.post(
        "/forms/export-all-transactions",
        {}, // request body
        {
          responseType: "blob",
          withCredentials: true, // send the CSRF cookie
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(csrfToken && {
              "X-XSRF-TOKEN": csrfToken,
              "X-CSRF-TOKEN": csrfToken,
            }),
          },
        }, // config
      );

      const url = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = url;
      link.download = `transactions_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);

      if (error?.response?.status === 429) {
        showError(
          "Too many export requests. Please wait a few minutes and try again.",
        );
      } else if (error?.response?.status === 403) {
        showError(
          "Export blocked (403). CSRF token missing or invalid — refresh the page and try again.",
        );
      } else if (error?.response?.status === 401) {
        showError("Session expired. Please log in again.");
      } else {
        showError("Failed to export transactions. Please try again.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Capsule count — API total once a capsule has been loaded; until then the
  // overall metrics (only valid while no search / date filter is applied)
  const renderCount = (key) => {
    let count = capsuleCounts[key];
    if (count === null) {
      const noFilters = !debouncedSearch && !startStr && !endStr;
      if (noFilters && !metricsLoading) {
        count = key === "all" ? metrics.total : metrics[key];
      }
    }
    return count !== null && count !== undefined ? (
      <span className="px-pill-count">{count}</span>
    ) : null;
  };

  return (
    <>
      <div className="px-shell">
        <Sidebar />
        <main className="px-main">
          <div className="px-page-head mb-4">
            <h2 className="px-title">Analytics</h2>
            <div className="px-sub">
              Transaction metrics & performance overview
            </div>
          </div>
          <Row className="mb-4 g-3">
            <Col xs="12" sm="6" xl="3">
              <div className="px-summary-item">
                <div className="px-summary-icon">
                  <FileText size={18} />
                </div>
                <div>
                  <span>Total Transactions</span>
                  <strong>{metricsLoading ? "…" : metrics.total}</strong>
                </div>
              </div>
            </Col>
            <Col xs="12" sm="6" xl="3">
              <div className="px-summary-item">
                <div className="px-summary-icon">
                  <CheckCircle size={18} color="#16a34a" />
                </div>
                <div>
                  <span>Successful Transactions</span>
                  <strong>{metricsLoading ? "…" : metrics.success}</strong>
                </div>
              </div>
            </Col>
            <Col xs="12" sm="6" xl="3">
              <div className="px-summary-item">
                <div className="px-summary-icon">
                  <Clock size={18} color="#f59e0b" />
                </div>
                <div>
                  <span>Pending Transactions</span>
                  <strong>{metricsLoading ? "…" : metrics.pending}</strong>
                </div>
              </div>
            </Col>
            <Col xs="12" sm="6" xl="3">
              <div className="px-summary-item">
                <div className="px-summary-icon">
                  <XCircle size={18} color="#dc2626" />
                </div>
                <div>
                  <span>Failed Transactions</span>
                  <strong>{metricsLoading ? "…" : metrics.failed}</strong>
                </div>
              </div>
            </Col>
          </Row>

          {/* Filters */}
          <Row className="align-items-center mb-2 mt-5">
            <Col className="d-flex gap-2 align-items-center flex-wrap">
              <div
                className={`px-filter px-filter-compact ${
                  search ? "has-value" : ""
                }`}
              >
                <Search size={16} className="px-search-icon" />
                <input
                  ref={searchRef}
                  className="px-input-compact"
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="px-clear-btn"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateChange}
                maxDate={new Date()}
                monthsShown={2}
                popperPlacement="bottom-start"
                customInput={
                  <DateRangeInput
                    startDate={startDate}
                    endDate={endDate}
                    onClear={() => handleDateChange([null, null])}
                  />
                }
              />

              <div className="px-status-pills">
                <button
                  className={`px-pill ${status === "all" ? "active" : ""}`}
                  onClick={() => handleStatusChange("all")}
                >
                  All {renderCount("all")}
                </button>
                <button
                  className={`px-pill success ${
                    status === "success" ? "active" : ""
                  }`}
                  onClick={() => handleStatusChange("success")}
                >
                  Success {renderCount("success")}
                </button>
                <button
                  className={`px-pill pending ${
                    status === "pending" ? "active" : ""
                  }`}
                  onClick={() => handleStatusChange("pending")}
                >
                  Pending {renderCount("pending")}
                </button>
                <button
                  className={`px-pill failed ${
                    status === "failed" ? "active" : ""
                  }`}
                  onClick={() => handleStatusChange("failed")}
                >
                  Failed {renderCount("failed")}
                </button>
              </div>
            </Col>

            <Col md="auto">
              <button
                className="px-export-btn"
                onClick={exportAllTransactions}
                disabled={isExporting}
                style={{
                  opacity: isExporting ? 0.7 : 1,
                  cursor: isExporting ? "not-allowed" : "pointer",
                }}
              >
                {isExporting ? (
                  <Spinner size="sm" color="light" />
                ) : (
                  <Download size={16} />
                )}
                <span>{isExporting ? "Exporting..." : "Export CSV"}</span>
              </button>
            </Col>
          </Row>

          {/* Data Table */}
          <Row>
            <Col>
              <div className="px-card master-table-wrap">
                <CardBody>
                  <div className="px-card-head">
                    <h4>Transaction Analytics</h4>
                    <span className="px-muted">
                      Showing {filteredData.length} of {totalRows} records
                    </span>
                  </div>
                  {isLoading && filteredData.length === 0 ? (
                    <SkeletonTable rows={8} cols={6} />
                  ) : (
                    <DataTable
                      columns={allColumns}
                      data={filteredData.map((row, idx) => ({
                        ...row,
                        ...labeledData[idx],
                      }))}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationDefaultPage={currentPage}
                      paginationResetDefaultPage={paginationReset}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={(page) => setCurrentPage(page)}
                      onChangeRowsPerPage={(perPage, page) => {
                        setRowsPerPage(perPage);
                        setCurrentPage(page);
                      }}
                      highlightOnHover
                      responsive
                      progressPending={isLoading}
                      progressComponent={
                        <div className="py-5 text-center">
                          <Spinner color="primary" size="sm" className="me-2" />
                          <span className="text-muted">Loading records…</span>
                        </div>
                      }
                      noDataComponent={
                        <div style={{ padding: 40, color: "#64748b" }}>
                          {"No transactions records found"}
                        </div>
                      }
                    />
                  )}
                </CardBody>
              </div>
            </Col>
          </Row>
        </main>
      </div>
    </>
  );
}
